import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Search, CircleMinus, CircleCheck, Save, Check, Lock, Edit2 } from 'lucide-react';
import itAssetsService, {
    ITAsset, ITAssetCreatePayload, AssetType, SharingMode,
} from '../../services/itAssetsService';
import { useCachedResource } from '../../contexts/MasterDataContext';
import { Posting } from '../../services/postingService';
import extractApiError from '../../utils/apiError';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import Skeleton from '../common/Skeleton';
import Badge from '../common/Badge';
import { usePermission } from '../../contexts/PermissionContext';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import ApprovalDot from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from '../../validation/ValidatedFields';
import { FieldSpec } from '../../validation/engine';

const RBAC_ACTIVITY = 'ORG_ITGC_ASSETS';
const ASSET_TYPES: AssetType[] = ['HARDWARE', 'SOFTWARE', 'NETWORK', 'PERIPHERAL', 'OTHER'];
const SHARING_MODES: { value: SharingMode; label: string }[] = [
    { value: 'SHARED', label: 'Shared (many users)' },
    { value: 'EXCLUSIVE', label: 'Exclusive (one user at a time)' },
];

interface LocationLite { id: number; name: string; }

// =============================================================================
// Form modal
// =============================================================================

type FormState = {
    id?: number;
    /** System-generated code (ITA-N) shown in the list; read-only in the form. */
    code?: string | null;
    asset_code: string;
    asset_name: string;
    description?: string;
    asset_type?: AssetType;
    sharing_mode?: SharingMode;
    serial_number?: string;
    make_model?: string;
    purchase_date?: string;
    warranty_expiry?: string;
    vendor_name?: string;
    location_id?: number;
    posting_id?: number | null;
    purpose?: string;
    is_active?: number;
    workflow_state?: string;
};

const EMPTY: FormState = {
    asset_code: '',
    asset_name: '',
    asset_type: 'HARDWARE',
    sharing_mode: 'SHARED',
};

const AssetFormModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    initial?: FormState;
    locations: LocationLite[];
    postings: Posting[];
    assets: ITAsset[];
    onSubmit: (form: FormState) => Promise<void>;
    onToggleActive?: () => Promise<void>;
    canModify?: boolean;
}> = ({ isOpen, onClose, initial, locations, postings, assets, onSubmit, onToggleActive, canModify = true }) => {
    const [form, setForm] = useState<FormState>(initial || EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    // View-first edit: existing records open read-only ("Viewing") until the
    // user clicks Edit, which flips this to true ("Editing").
    const [isEditing, setIsEditing] = useState(false);
    const assetVh = useFieldValidation();

    const isApproved = initial?.workflow_state === 'approved';
    // Existing records are read-only until Edit is clicked; create is always editable.
    const fieldsDisabled = !!initial?.id && (!canModify || isApproved || !isEditing);

    useEffect(() => {
        setForm(initial || EMPTY);
        assetVh.reset();
        setSubmitting(false);
        setToggling(false);
        setIsEditing(false);   // every open starts in read-only "Viewing" mode
    }, [initial, isOpen]);

    // Domain-level checks that must coexist with engine format errors.
    const runDomainChecks = (): boolean => {
        let ok = true;
        // asset_code (the ITA-N Code) is auto-generated server-side on create —
        // it is never user-entered, so no uniqueness check is needed here.
        if (!form.location_id) {
            assetVh.setError('location_id', 'Location is required');
            ok = false;
        }
        if (form.purchase_date) {
            const today = new Date(); today.setHours(0,0,0,0);
            if (new Date(form.purchase_date) > today) {
                assetVh.setError('purchase_date', 'Purchase date cannot be in the future');
                ok = false;
            }
        }
        return ok;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const specs: FieldSpec[] = [
            { name: 'asset_name', code: 109, value: form.asset_name, required: true },
            { name: 'make_model', code: 109, value: form.make_model },
            { name: 'serial_number', code: 109, value: form.serial_number },
            { name: 'vendor_name', code: 109, value: form.vendor_name },
            { name: 'purpose', code: 110, value: form.purpose },
            { name: 'purchase_date', code: 305, value: form.purchase_date },
            { name: 'warranty_expiry', code: 305, value: form.warranty_expiry },
        ];
        if (form.purchase_date && form.warranty_expiry) {
            specs.push({
                name: 'warranty_expiry', code: 601, value: 'x', required: true,
                additional: { from_date: form.purchase_date, to_date: form.warranty_expiry },
            });
        }
        const formatOk = assetVh.validateAll(specs);
        const domainOk = runDomainChecks();
        if (!formatOk || !domainOk) {
            return;
        }
        setSubmitting(true);
        try { await onSubmit(form); }
        catch (e: any) { /* parent shows toast */ }
        finally { setSubmitting(false); }
    };

    const handleToggle = async () => {
        if (!onToggleActive) return;
        setToggling(true);
        try { await onToggleActive(); } finally { setToggling(false); }
    };

    let headerText: React.ReactNode;
    if (isApproved) {
        headerText = <><Lock size={14} className="inline mr-1 text-green-600" />Viewing "<span className="item-highlight">{initial?.asset_name}</span>" (Approved)</>;
    } else if (initial?.id) {
        headerText = <>{isEditing ? 'Editing' : 'Viewing'} "<span className="item-highlight">{initial.asset_name}</span>"</>;
    } else {
        headerText = 'Create IT Asset';
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" data-testid="MOD_it_asset_form">
            <Modal.Header>{headerText}</Modal.Header>
            <Modal.Body>
                {isApproved && (
                    <div className="approved-banner" data-testid="BANNER_it_asset_locked">
                        <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                    </div>
                )}
                <form
                    id="it-asset-form"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-y-4"
                >
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        {/* System code (ITA-N) — auto-generated by the workflow, never
                            user-entered. Disabled on create (placeholder until approved)
                            and read-only in edit. Mirrors the workflow-code pattern used
                            on Departments / Procedures. */}
                        <FormControl.Input
                            label="Code"
                            value={form.code || ''}
                            placeholder="—"
                            disabled
                            data-testid="INP_it_asset_code"
                        />
                        <FormControl.SearchSelect
                            label="Asset Type"
                            value={form.asset_type || 'HARDWARE'}
                            onChange={(value) => setForm({ ...form, asset_type: value as AssetType })}
                            options={ASSET_TYPES.map(t => ({ value: t, label: t }))}
                            disabled={fieldsDisabled}
                            data-testid="SEL_it_asset_type"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-x-6 gap-y-4 items-center">
                        <FormControl.SearchSelect
                            label="Sharing Mode"
                            value={form.sharing_mode || 'SHARED'}
                            onChange={(value) => setForm({ ...form, sharing_mode: value as SharingMode })}
                            options={SHARING_MODES}
                            disabled={fieldsDisabled}
                            data-testid="SEL_it_asset_sharing_mode"
                        />
                        <p className="text-xs text-slate-500 mt-5">
                            <strong>Exclusive</strong> assets (e.g. a laptop) can be held by only one
                            person at a time; a new grant is blocked until access is revoked.
                            <strong> Shared</strong> assets (e.g. a server) may be granted to many.
                        </p>
                    </div>

                    <ValidatedInput
                        vh={assetVh}
                        name="asset_name"
                        code={109}
                        required
                        label="Asset Name"
                        type="text"
                        value={form.asset_name}
                        onChange={e => setForm({ ...form, asset_name: e.target.value })}
                        disabled={fieldsDisabled}
                        data-testid="INP_it_asset_name"
                    />

                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <ValidatedInput
                            vh={assetVh}
                            name="make_model"
                            code={109}
                            label="Make / Model"
                            type="text"
                            value={form.make_model || ''}
                            onChange={e => setForm({ ...form, make_model: e.target.value })}
                            placeholder="e.g. Dell Latitude 7440"
                            disabled={fieldsDisabled}
                            data-testid="INP_it_asset_make_model"
                        />
                        <ValidatedInput
                            vh={assetVh}
                            name="serial_number"
                            code={109}
                            label="Serial Number"
                            type="text"
                            value={form.serial_number || ''}
                            onChange={e => setForm({ ...form, serial_number: e.target.value })}
                            disabled={fieldsDisabled}
                            data-testid="INP_it_asset_serial_number"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <ValidatedDatePicker
                            vh={assetVh}
                            name="purchase_date"
                            code={305}
                            label="Purchase Date"
                            value={form.purchase_date || ''}
                            onChange={e => setForm({ ...form, purchase_date: e.target.value })}
                            disabled={fieldsDisabled}
                            data-testid="INP_it_asset_purchase_date"
                        />
                        <ValidatedDatePicker
                            vh={assetVh}
                            name="warranty_expiry"
                            code={305}
                            label="Warranty Expiry"
                            value={form.warranty_expiry || ''}
                            onChange={e => setForm({ ...form, warranty_expiry: e.target.value })}
                            disabled={fieldsDisabled}
                            data-testid="INP_it_asset_warranty_expiry"
                        />
                    </div>

                    <ValidatedInput
                        vh={assetVh}
                        name="vendor_name"
                        code={109}
                        label="Vendor"
                        type="text"
                        value={form.vendor_name || ''}
                        onChange={e => setForm({ ...form, vendor_name: e.target.value })}
                        placeholder="Free text"
                        disabled={fieldsDisabled}
                        data-testid="INP_it_asset_vendor"
                    />

                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <FormControl.SearchSelect
                            label="Location"
                            required
                            value={form.location_id}
                            onChange={(value) => {
                                setForm({ ...form, location_id: value ? Number(value) : undefined });
                                assetVh.clearError('location_id');
                            }}
                            options={locations.map(l => ({ value: l.id, label: l.name }))}
                            placeholder="— Select location —"
                            clearable
                            error={assetVh.errors.location_id}
                            disabled={fieldsDisabled}
                            data-testid="SEL_it_asset_location"
                        />
                        <FormControl.SearchSelect
                            label="Assigned to (Posting)"
                            value={form.posting_id}
                            onChange={(value) => setForm({ ...form, posting_id: value ? Number(value) : null })}
                            options={postings.map(p => ({
                                value: p.id,
                                label: `${p.user_name || `User#${p.user_id}`} @ ${p.position_name || p.position_code || `Pos#${p.position_id}`}`,
                            }))}
                            placeholder="— Unassigned —"
                            clearable
                            disabled={fieldsDisabled}
                            data-testid="SEL_it_asset_posting"
                        />
                    </div>

                    <ValidatedTextarea
                        vh={assetVh}
                        name="purpose"
                        code={110}
                        label="Purpose"
                        rows={3}
                        value={form.purpose || ''}
                        onChange={e => setForm({ ...form, purpose: e.target.value })}
                        placeholder="What business purpose does this asset serve?"
                        disabled={fieldsDisabled}
                        data-testid="INP_it_asset_purpose"
                    />

                    <FormControl.Textarea
                        label="Description"
                        rows={3}
                        value={form.description || ''}
                        onChange={e => setForm({ ...form, description: e.target.value })}
                        disabled={fieldsDisabled}
                        data-testid="INP_it_asset_description"
                    />
                </form>
            </Modal.Body>
            <Modal.Footer className={initial?.id ? 'app-modal-footer--split' : ''}>
                {/* Active/Inactive toggle — shown for existing non-approved records (viewing + editing) */}
                {initial?.id && onToggleActive && !isApproved && (
                    <Button
                        variant={initial.is_active ? 'inactive-outline' : 'active-outline'}
                        icon={initial.is_active ? CircleMinus : CircleCheck}
                        onClick={handleToggle}
                        disabled={toggling || submitting}
                        data-testid="BTN_it_asset_toggle_active"
                    >
                        {toggling ? '...' : initial.is_active ? 'Inactive' : 'Active'}
                    </Button>
                )}
                {/* View → Edit: existing record, not yet editing (brand color) */}
                {initial?.id && canModify && !isApproved && !isEditing && (
                    <Button
                        variant="fill"
                        icon={Edit2}
                        onClick={() => setIsEditing(true)}
                        data-testid="BTN_it_asset_edit"
                    >
                        Edit
                    </Button>
                )}
                {/* Submit (create) or Update (existing + editing) */}
                {(!isApproved && canModify && (!initial?.id || isEditing)) && (
                    <Button
                        type="submit"
                        form="it-asset-form"
                        variant={initial?.id ? 'update' : 'fill'}
                        icon={initial?.id ? Check : Save}
                        disabled={submitting || toggling}
                        data-testid="BTN_it_asset_submit"
                    >
                        {submitting
                            ? (initial?.id ? 'Updating...' : 'Submitting...')
                            : (initial?.id ? 'Update' : 'Submit')}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
};

// =============================================================================
// Main
// =============================================================================

const ITAssetManagement: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
    // ── RBAC gates ────────────────────────────────────────────────────────
    const viewPerm    = usePermission(RBAC_ACTIVITY, 'view');
    const modifyPerm  = usePermission(RBAC_ACTIVITY, 'view+modify');
    const canView     = viewPerm.allowed;
    const canModify   = modifyPerm.allowed;
    const permsLoading = viewPerm.loading;

    const [assets, setAssets] = useState<ITAsset[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState<AssetType | 'ALL'>('ALL');
    const [locationFilter, setLocationFilter] = useState<number | 'ALL'>('ALL');
    const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editing, setEditing] = useState<FormState | undefined>(undefined);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [workflowOpenFor, setWorkflowOpenFor] = useState<ITAsset | null>(null);

    const { data: locationsRaw } = useCachedResource<{ id: number; name: string }>(
        'locations',
    );
    const { data: postingsRaw } = useCachedResource<Posting>('postings', {
        enabled: isFormOpen,
    });
    const locations: LocationLite[] = (locationsRaw ?? []).map(l => ({ id: l.id, name: l.name }));
    // Only approved postings belong in the "Assigned to" picker — the postings
    // cache returns all states (getAll returns draft/unapproved rows too).
    const postings: Posting[] = (postingsRaw ?? []).filter(p => (p.is_approved ?? 0) === 1);

    useEffect(() => { setPage(1); }, [searchTerm, typeFilter, locationFilter, statusFilter, pageSize]);

    const filteredAssets = [...assets]
        .filter(a => {
            const active = Number((a as any).is_active) === 1;
            return statusFilter === 'ACTIVE' ? active : !active;
        })
        .sort((a, b) => (b.id || 0) - (a.id || 0));
    const totalRecords = filteredAssets.length;
    const paginatedAssets = filteredAssets.slice((page - 1) * pageSize, page * pageSize);

    const refresh = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        try {
            const a = await itAssetsService.list({
                search: searchTerm || undefined,
                asset_type: typeFilter === 'ALL' ? undefined : typeFilter,
                location_id: locationFilter === 'ALL' ? undefined : locationFilter,
                status: statusFilter,
            });
            setAssets(a);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load assets') });
        } finally {
            setLoading(false);
        }
    }, [searchTerm, typeFilter, locationFilter, statusFilter, canView]);

    useEffect(() => { refresh(); }, [refresh]);

    const handleCreate = () => {
        if (!canModify) return;
        setEditing(undefined);
        setIsFormOpen(true);
    };

    const handleEdit = (a: ITAsset) => {
        setEditing({
            id: a.id,
            code: a.code,
            asset_code: a.asset_code,
            asset_name: a.asset_name,
            description: a.description,
            asset_type: a.asset_type,
            sharing_mode: a.sharing_mode,
            serial_number: a.serial_number,
            make_model: a.make_model,
            purchase_date: a.purchase_date,
            warranty_expiry: a.warranty_expiry,
            vendor_name: a.vendor_name,
            location_id: a.location_id,
            posting_id: a.posting_id ?? null,
            purpose: a.purpose,
            is_active: a.is_active,
            workflow_state: a.workflow_state,
        });
        setIsFormOpen(true);
    };

    const handleSubmitForm = async (form: FormState) => {
        const payload: ITAssetCreatePayload = {
            // asset_code omitted — the backend auto-generates it (ITA-N) on create.
            asset_name: form.asset_name,
            description: form.description,
            asset_type: form.asset_type,
            sharing_mode: form.sharing_mode,
            serial_number: form.serial_number,
            make_model: form.make_model,
            purchase_date: form.purchase_date,
            warranty_expiry: form.warranty_expiry,
            vendor_name: form.vendor_name,
            location_id: form.location_id!,
            posting_id: form.posting_id ?? null,
            purpose: form.purpose,
        };
        if (form.id) {
            await itAssetsService.update(form.id, payload);
            setToast({ type: 'success', message: 'Asset updated successfully.' });
        } else {
            await itAssetsService.create(payload);
            setToast({ type: 'success', message: 'Asset created successfully.' });
        }
        setIsFormOpen(false);
        await refresh();
    };

    const handleToggleActive = async () => {
        if (!editing?.id) return;
        const next = editing.is_active ? 0 : 1;
        const confirmFn = editing.is_active ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? 'Reactivate this asset?' : 'Inactivate this asset?',
            text: next
                ? 'It will appear in active lists again.'
                : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        try {
            await itAssetsService.update(editing.id, { is_active: next });
            setIsFormOpen(false);
            await refresh();
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Asset Reactivated.' : 'Asset Inactivated.' });
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to update asset status.') });
        }
    };

    // ── Workflow ──────────────────────────────────────────────────────────
    // submit/approve/reject/reopen are driven end-to-end by <WorkflowModal>
    // via the /api/v1/wf engine (kind="org_it_asset").
    const closeWfModal = () => setWorkflowOpenFor(null);

    // ── Not-authorised gate ───────────────────────────────────────────────
    if (!permsLoading && !canView) {
        return (
            <div className={`netbody-card flex flex-col h-full ${compact ? '' : 'min-h-[500px]'}`}>
                <NoDataFound
                    title="Not authorised"
                    message={viewPerm.message || 'You are not authorised to view IT Assets. Please contact your Super Admin.'}
                />
            </div>
        );
    }

    return (
        <div className={`netbody-card flex flex-col h-full ${compact ? '' : 'min-h-[500px]'}`}>

            <div className="netbody-header">
                {/* Status — iOS-style on/off switch (on = active) */}
                <StatusSwitch
                    value={statusFilter === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE'}
                    onChange={next => setStatusFilter(next)}
                    noun="assets"
                    testId="SEL_it_asset_status_filter"
                />
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.SearchSelect
                        value={typeFilter === 'ALL' ? null : typeFilter}
                        onChange={v => setTypeFilter(v == null ? 'ALL' : v as AssetType)}
                        options={[{ value: 'ALL', label: 'All types' }, ...ASSET_TYPES.map(t => ({ value: t, label: t }))]}
                        placeholder="All types"
                        clearable
                        noMargin
                        data-testid="SEL_it_asset_type_filter"
                    />
                    <FormControl.SearchSelect
                        value={locationFilter === 'ALL' ? null : locationFilter}
                        onChange={v => setLocationFilter(v == null ? 'ALL' : Number(v))}
                        options={locations.map(l => ({ value: l.id, label: l.name }))}
                        placeholder="All locations"
                        clearable
                        noMargin
                        data-testid="SEL_it_asset_location_filter"
                    />
                    <FormControl.Input
                        type="text"
                        placeholder="Search assets…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_it_asset_search"
                    />
                    {canModify && (
                        <Button variant="fill" icon={Plus} onClick={handleCreate} data-testid="BTN_it_asset_create">
                            Create
                        </Button>
                    )}
                </div>
            </div>

            <div className="netbody-content">

                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--dot"></th>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Code</th>
                                        <th className="table-list-th">Asset</th>
                                        <th className="table-list-th">Type</th>
                                        <th className="table-list-th">Sharing</th>
                                        <th className="table-list-th">Location</th>
                                        <th className="table-list-th">Assigned</th>
                                        <th className="table-list-th">Purpose</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['dot', '3xs', '2xs', 'md', 'pill', 'pill', 'sm', 'sm', 'md']} />}
                                    {!loading && filteredAssets.length === 0 && (
                                        <tr>
                                            <td colSpan={9} className="table-list-cell">
                                                <NoDataFound
                                                    title="No IT Assets Found"
                                                    message='No IT assets yet. Click "Create" to register one.'
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && paginatedAssets.map((a, idx) => {
                                        const active = Number((a as any).is_active) === 1;
                                        return (
                                        <tr key={a.id} className={`table-list-row${active ? '' : ' is-inactive'}`} data-testid={`BTN_it_asset_row_${a.id}_edit`} onClick={() => handleEdit(a)}>
                                            <td className="table-list-cell table-list-cell--dot" data-testid={`BTN_it_asset_row_${a.id}_workflow`} onClick={e => { e.stopPropagation(); setWorkflowOpenFor(a); }}>
                                                <ApprovalDot state={a.workflow_state || 'draft'} onClick={() => setWorkflowOpenFor(a)} title="Workflow — view timeline & act" />
                                            </td>
                                            <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                            <td className="table-list-cell" data-testid={`TXT_it_asset_row_${a.id}_code`}>{a.code ? <span className="cell-code">{a.code}</span> : <span className="text-muted">—</span>}</td>
                                            <td className="table-list-cell">
                                                <span className="table-list-cell-title cell-name-lock">
                                                    {a.workflow_state === 'approved' && <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />}
                                                    {a.asset_name}
                                                </span>
                                                {a.make_model && <span className="table-list-cell-sub">{a.make_model}{a.serial_number && ` · SN: ${a.serial_number}`}</span>}
                                            </td>
                                            <td className="table-list-cell">
                                                <Badge variant="info" size="sm">{a.asset_type}</Badge>
                                            </td>
                                            <td className="table-list-cell">
                                                <Badge variant={a.sharing_mode === 'EXCLUSIVE' ? 'warning' : 'success'} size="sm">
                                                    {a.sharing_mode === 'EXCLUSIVE' ? 'Exclusive' : 'Shared'}
                                                </Badge>
                                            </td>
                                            <td className="table-list-cell">{a.location_name || '—'}</td>
                                            <td className="table-list-cell">{a.posting_label || <span className="italic text-slate-400">unassigned</span>}</td>
                                            <td className="table-list-cell table-list-cell--truncate" title={a.purpose || ''}>{a.purpose || '—'}</td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="assets"
                        />
                    </div>
                </div>
            </div>

            <Toast toast={toast} onClose={() => setToast(null)} />
            <AssetFormModal
                isOpen={isFormOpen}
                onClose={() => setIsFormOpen(false)}
                initial={editing}
                locations={locations}
                postings={postings}
                assets={assets}
                onSubmit={handleSubmitForm}
                onToggleActive={editing ? handleToggleActive : undefined}
                canModify={canModify}
            />
            <WorkflowModal
                isOpen={!!workflowOpenFor}
                onClose={closeWfModal}
                kind="org_it_asset"
                id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
                onChanged={refresh}
            />
        </div>
    );
};

export default ITAssetManagement;
